import { EVENT_HOST_UNLOAD, EVENT_INFO, EVENT_LOAD } from 'sidebar/app/sidebar/events';

import { PageInfo } from 'sidebar/app/sidebar/sidebar_loader';

/*
 * Important! Bump this version number for each deploy. Doing so will
 * inform users that they have an older version and should update with
 * instructions on how to do so.
 */
export const SIDEBAR_VERSION = 4;

declare global {
  interface Window {
    uvOpenedInPopup: boolean;
    uvSidebarWindow: Window;
  }
}

interface ExtendedBookmarketNode extends NamedNodeMap {
  'data-uv-sidebar-bookmarklet': {
    value: string
  };
}

const wrapForBookmark = (src: string) => {
  return encodeURIComponent(
    `(${src})();`
      // Allow comments in the bookmarklet code.
      // Without this the bookmarklet would compile, but error.
      .replace(/^\s*\/\/.*$/gm, '')
      // Remove unnecessary spaces
      .replace(/'[\r\n]|[ \t]{2,}/g, '')
  );
};

const createBookmarkletFor = (host: string) => {
  const initCode = () => {
    const t = new Date().getTime();
    const sidebarHost = '##HOST##';
    const sidebarVersion = '##VERSION##';
    const eventMethod = window.addEventListener ? 'addEventListener' : 'attachEvent';
    // Add version to URL so index.ts can parse it to check for freshness
    const sidebarPage = `https://${sidebarHost}/clients/agent#?v=${sidebarVersion}&popup=1`;

    const openInPopup = () => {
      window.uvOpenedInPopup = true;

      let eventer: any = (window as any)[eventMethod];
      let messageEvent = eventMethod === 'attachEvent' ? 'onmessage' : 'message';

      if (
        window.uvSidebarWindow == undefined ||
        window.uvSidebarWindow == null ||
        window.uvSidebarWindow.closed
      ) {
        const width = 320;
        const height = window.innerHeight;
        // Note: left is zerod at the left edge of the users's monitor/workspace, *not* the browser window.
        const left = (window.screenLeft || 0) + window.outerWidth - width;
        const windowOptions = `dialog=yes,alwaysRaised=yes,menubar=no,location=no,resizable=no,scrollbars=yes,status=no,top=0,width=${width},height=${height},left=${left}`;

        // Hack to get postMessage working in IE11. Leave this alone!
        window.uvSidebarWindow = window.open(`/`, `UserVoice Sidebar`, windowOptions);
        if (window.uvSidebarWindow) {
          // Second part of IE11 hack to get postMessage working.
          window.uvSidebarWindow.location.href = 'about:blank';
          window.uvSidebarWindow.location.href = sidebarPage;
        } else {
          // Couldn't load the window using the IE11 hack. Try normally.
          window.uvSidebarWindow = window.open(sidebarPage, `UserVoice Sidebar`, windowOptions);
          if (!window.uvSidebarWindow) {
            // Blocked due to browser settings or Safari's dumb blocking of opening windows from an asynchronous action.
            alert(`The UserVoice Contributor Sidebar was unable to load on this website.\n\nTry clicking the bookmark again. You may need to disable your pop-up window blocker for this site.`);
          }
        }
      } else {
        window.uvSidebarWindow.focus();
      }

      const throttle = (callback: any, limit: number) => {
        let wait = false;
        return () => {
          if (!wait) {
            callback.call();
            wait = true;
            setTimeout(() => {
              wait = false;
            }, limit);
          }
        };
      };

      // Tests if two PageInfos are the same.
      // This doesn't need to be a perfect isEqual.
      const isEqual = (a: any, b: any) => {
        if (typeof a !== 'object' || typeof b !== 'object') {
          return false;
        }
        return Object.keys(a).every((key: string) => {
          if (typeof a[key] === 'string') {
            if (a[key] !== b[key]) {
              return false;
            }
          } else {
            return a[key] === b[key];
          }
        });
      };

      const sendPageInfoToPopup = (message: PageInfo & { __type?: string }) => {
        message.__type = '##EVENT_INFO##';
        window.uvSidebarWindow.postMessage(message, '*');
      };

      const sendUnloadMessageToPopup = () => {
        window.uvSidebarWindow.postMessage({ __type: '##EVENT_HOST_UNLOAD##' }, '*');
      };

      const getHostEmails = () => {
        const bodyText = document.body.innerText || document.body.textContent;
        return bodyText.match(/\b(\w[-'`+.\w]*@([-\w]*\.)+[a-zA-Z]{2,32})\b/g);
      };

      const getPageInfo = (): PageInfo => {
        return {
          pageUrl: location.href,
          pageTitle: document.title,
          sourceUrl: location.href,
          selectedText: window.getSelection().toString() || '',
          emails: getHostEmails()
        };
      };

      let pageInfo: PageInfo;

      const sendPageInfo = (forceUpdate: any = false) => {
        const currentInfo = getPageInfo();
        const updated = !isEqual(pageInfo, currentInfo);
        if (updated || forceUpdate === true) {
          sendPageInfoToPopup(currentInfo);
          pageInfo = currentInfo;
        }
      };

      const sendPageInfoInstance = throttle(sendPageInfo, 300);
      const pageObserver = window.MutationObserver ? new MutationObserver(sendPageInfoInstance) : undefined;

      const checkSelection = () => {
        const selection = window.getSelection().toString();
        if (selection !== pageInfo.selectedText) { sendPageInfo(); }
      };

      const onContentLoad = () => {
        if (pageObserver) {
          pageObserver.observe(document.body, { childList: true, subtree: true });
        }
        window.addEventListener('hashchange', sendPageInfoInstance);
        window.addEventListener('popstate', sendPageInfoInstance);
        window.addEventListener('mouseup', checkSelection);
        window.addEventListener('unload', sendUnloadMessageToPopup);
        sendPageInfo(true);
      };

      const receiveMessage = (event: any) => {
        if (event.origin === 'https://##HOST##' && event.data.__type === '##EVENT_LOAD##') {
          onContentLoad();
        }
      };

      eventer(messageEvent, receiveMessage, false);
    };

    if (window.uvAgentSidebar) {
      window.uvAgentSidebar.init();
    } else {
      if (!document.getElementById('uv-agent-sidebar-loader')) {
        var src : string | TrustedScriptURL = `https://${sidebarHost}/clients/agent/sidebar_loader.js?t=${t}`;
        if (window.trustedTypes) {
          const policy = window.trustedTypes.createPolicy('scriptPolicy', {
            createScriptURL: (input: string) => {
              return input;
            },
          });
          src = policy.createScriptURL(src);
        }

        let s = document.createElement('script');
        s.id = 'uv-agent-sidebar-loader';
        s.setAttribute('data-subdomain', sidebarHost);
        s.setAttribute('data-bookmarklet-version', sidebarVersion);
        s.setAttribute('type', 'text/javascript');
        s.onload = () => { window.uvAgentSidebar.init(); };
        s.src = src as string;
        (s as any)[eventMethod]('error', () => {
          openInPopup();
        }, false);
        document.body.appendChild(s);
      } else if (window.uvOpenedInPopup) {
        openInPopup();
      }
    }
  };

  // The code above has to be converted to a string so that it can
  // be made into a bookmarklet. This bakes the host and version
  // info into the code since the code won't have access to this
  // closure when it's run.
  const initCodeString = initCode.toString()
    .replace(/##HOST##/g, host)
    .replace(/##VERSION##/g, '' + SIDEBAR_VERSION)
    .replace(/##EVENT_INFO##/g, EVENT_INFO)
    .replace(/##EVENT_LOAD##/g, EVENT_LOAD)
    .replace(/##EVENT_HOST_UNLOAD##/g, EVENT_HOST_UNLOAD);

  return wrapForBookmark(initCodeString);
};

const nodes = document.querySelectorAll('[data-uv-sidebar-bookmarklet]');
Array.prototype.forEach.call(nodes, (node: HTMLAnchorElement) => {
  const map = node.attributes as ExtendedBookmarketNode;
  const host = map['data-uv-sidebar-bookmarklet'].value;
  node.href = 'javascript:' + createBookmarkletFor(host);
});
